import { useMemo } from 'react';
import type { RenderableCode } from 'ts/commons/code/RenderedCode.context';
import { applyDiffChangeRegions, type CodeDiff } from 'ts/commons/code/RenderedCode.diff';
import { StyleRegion } from 'ts/commons/code/StyleRegion';
import { ObjectUtils } from 'ts/commons/ObjectUtils';
import { StringUtils } from 'ts/commons/StringUtils';
import { ETokenStyleClass } from 'typedefs/ETokenStyleClass';
import type { PreprocessorExpansionsTransport } from 'typedefs/PreprocessorExpansionsTransport';
import { Links } from '../links/Links';
import tokenStyles from './token-styles.module.less';

/** A segment of text within a code line. */
export type LineSegment = BasicLineSegment | CodeLineSegment;

/** A single segment within a code line. */
type BasicLineSegment = {
	text: string;
	offsetInFile: number;
	fileAndOffsetId: string;
	associatedBracketSegmentId?: string;
};

/**
 * A single code segment (think: token) within a line. In some cases, this might also be only a part of a token (i.e.,
 * diff view).
 */
type CodeLineSegment = BasicLineSegment & {
	cssStyleClass: string;
	href?: string;
	isIdentifier: boolean;
	isDelimiter: boolean;
};

/** ETokenStyleClass values that represent identifier-like tokens (hoverable for navigation). */
const IDENTIFIER_LIKE: ReadonlySet<ETokenStyleClass> = new Set([
	ETokenStyleClass.IDENTIFIER,
	ETokenStyleClass.ANNOTATION,
	ETokenStyleClass.COMMENT_LINK,
	ETokenStyleClass.BROKEN_COMMENT_LINK
]);

/**
 * Computes lists of CodeLineSegment or BasicLineSegment for each line that contains code. CodeLineSegments hold the
 * formatting information for token highlighting, etc.
 */
export function useLineSegments(
	lines: string[],
	codeElement: RenderableCode,
	lineStartOffsets: number[],
	project: string,
	preprocessorExpansions: PreprocessorExpansionsTransport | undefined,
	diff: CodeDiff | undefined
) {
	return useMemo(() => {
		let styleRegions = buildStyleRegions(
			codeElement.tokens,
			determineRequiredPreprocessorOffsets(preprocessorExpansions)
		);
		if (diff !== undefined) {
			styleRegions = applyDiffChangeRegions(styleRegions, diff);
		}
		const groupedStyleRegions = groupStyleRegionsByLine(lineStartOffsets, styleRegions);
		const linesWithTokens = new Map<number, LineSegment[]>();
		lines.forEach((line, i) => {
			const lineNumber = i + 1;
			linesWithTokens.set(
				lineNumber,
				createLineSegments(line, groupedStyleRegions[i]!, lineStartOffsets[i]!, project, preprocessorExpansions)
			);
		});
		markBracesThatBelongTogether(linesWithTokens);
		return linesWithTokens;
	}, [project, codeElement.tokens, preprocessorExpansions, diff, lines, lineStartOffsets]);
}

/**
 * Builds an array of sequences/regions of consecutive tokens that have the same token style class from the given tokens
 * array. Each token is encoded as 3 ints: (start offset, end offset, ETokenStyleClass ordinal). Merges consecutive
 * tokens with the same style, but does not merge identifiers and delimiters (to allow highlighting).
 *
 * @param tokens The current list of tokens (3 ints per token: start offset, end offset, style class ordinal)
 * @param requiredOffsets Offsets that must not be merged with preceding tokens
 * @returns The style regions array.
 */
function buildStyleRegions(tokens: number[], requiredOffsets: number[]): StyleRegion[] {
	if (tokens.length === 0) {
		return [new StyleRegion(0, 0, '', ETokenStyleClass.PLAIN_TEXT)];
	}
	const styleRegions: StyleRegion[] = [];
	let lastInsertedIsIdentifierOrDelimiter = false;
	let lastOrdinal: number | undefined;
	for (let i = 0; i < tokens.length; i += 3) {
		const currentTokenStartOffset = tokens[i]!;
		const ordinal = tokens[i + 2]!;
		const styleClass = ETokenStyleClass.values[ordinal]!;
		const cssClass = tokenStyles[styleClass.name] ?? '';
		const isIdentifier = IDENTIFIER_LIKE.has(styleClass);
		const isDelimiter = styleClass === ETokenStyleClass.DELIMITER;
		const anyIsIdentifierOrDelimiter = isIdentifier || isDelimiter || lastInsertedIsIdentifierOrDelimiter;
		if (
			!anyIsIdentifierOrDelimiter &&
			ordinal === lastOrdinal &&
			!requiredOffsets.includes(currentTokenStartOffset)
		) {
			// Same style as previous token
			styleRegions[styleRegions.length - 1]!.endOffset = tokens[i + 1]!;
		} else {
			// New style class
			styleRegions.push(new StyleRegion(currentTokenStartOffset, tokens[i + 1]!, cssClass, styleClass));
			lastInsertedIsIdentifierOrDelimiter = isIdentifier || isDelimiter;
			lastOrdinal = ordinal;
		}
	}
	// Extend last region to "infinity", this makes the rendering code a lot
	// easier. There might be characters at the end of the file that are not
	// parsed as tokens here, these will use the style of the last region handled
	// here.
	styleRegions[styleRegions.length - 1]!.endOffset = 1e9;
	return styleRegions;
}

/**
 * Returns offsets that must not be merged with the preceding code because other parts of the Code view rely on the fact
 * that these offsets can be referred to.
 *
 * For example, the start-offsets of preprocessor expansions must be available in span ids such that the corresponding
 * listener can find these spans.
 */
function determineRequiredPreprocessorOffsets(
	preprocessorExpansions?: PreprocessorExpansionsTransport | null
): number[] {
	if (!preprocessorExpansions) {
		return [];
	}
	return ObjectUtils.numberKeys(preprocessorExpansions.offsetToExpansionGroup);
}

function groupStyleRegionsByLine(lineStartOffsets: number[], styleRegions: StyleRegion[]) {
	let currentTokenStyleRegionIndex = 0;
	let currentStyleRegion = styleRegions[currentTokenStyleRegionIndex];
	const groupedStyleRegions: StyleRegion[][] = [];
	for (let i = 0; i < lineStartOffsets.length; i++) {
		const nextLineStartOffset = i + 1 < lineStartOffsets.length ? lineStartOffsets[i + 1]! : Infinity;
		const regions = [];
		while (currentStyleRegion != null && currentStyleRegion.startOffset <= nextLineStartOffset) {
			regions.push(currentStyleRegion);
			if (currentStyleRegion.endOffset >= nextLineStartOffset) {
				break;
			}
			currentTokenStyleRegionIndex++;
			currentStyleRegion = styleRegions[currentTokenStyleRegionIndex];
		}
		groupedStyleRegions.push(regions);
	}
	return groupedStyleRegions;
}

function createLineSegments(
	lineContent: string,
	styleRegions: StyleRegion[],
	lineStartOffset: number,
	project: string,
	preprocessorExpansions?: PreprocessorExpansionsTransport
): LineSegment[] {
	const lineLength = lineContent.length;
	let offsetInCurrentLine = 0;
	let styleRegionIndex = 0;
	let currentTokenStyleRegion = styleRegions[styleRegionIndex];

	const lineSegments: LineSegment[] = [];
	while (offsetInCurrentLine < lineLength) {
		// There is still text to add for the current line. The loop can't run endlessly because we add at least one char in each iteration (and increment this.localOffset).
		if (currentTokenStyleRegion == null) {
			// Either there were no style regions or we are beyond the last region. Add the entire line, without formatting and return.
			lineSegments.push(createBasicLineSegment(lineContent, offsetInCurrentLine, lineLength, lineStartOffset));
			return lineSegments;
		}
		const lengthToNextStyleRegion = computeNumberOfLineCharsUntilOffset(
			currentTokenStyleRegion.startOffset,
			offsetInCurrentLine,
			lineStartOffset,
			lineLength
		);
		if (lengthToNextStyleRegion > 0) {
			// There is non-token text (or whitespace) before the current region. Add only that text in the current iteration.
			lineSegments.push(
				createBasicLineSegment(lineContent, offsetInCurrentLine, lengthToNextStyleRegion, lineStartOffset)
			);
			offsetInCurrentLine += lengthToNextStyleRegion;
		} else {
			const lengthToStyleRegionEnd =
				computeNumberOfLineCharsUntilOffset(
					currentTokenStyleRegion.endOffset,
					offsetInCurrentLine,
					lineStartOffset,
					lineLength
				) +
				// +1 because we want to add the char with the endOffset (in addition to the chars before it)
				1;
			const charOffsetInFile = lineStartOffset + offsetInCurrentLine;
			const segmentText = lineContent.substring(
				offsetInCurrentLine,
				offsetInCurrentLine + lengthToStyleRegionEnd
			);
			if (StringUtils.isEmptyOrWhitespace(segmentText)) {
				lineSegments.push(
					createBasicLineSegment(lineContent, offsetInCurrentLine, lengthToStyleRegionEnd, lineStartOffset)
				);
			} else {
				const isIdentifier = IDENTIFIER_LIKE.has(currentTokenStyleRegion.tokenStyleClass);
				const isDelimiter = currentTokenStyleRegion.tokenStyleClass === ETokenStyleClass.DELIMITER;
				lineSegments.push(
					createCodeLineSegment(
						segmentText,
						charOffsetInFile,
						project,
						currentTokenStyleRegion.styleCssClassName,
						isIdentifier,
						isDelimiter,
						preprocessorExpansions
					)
				);
			}
			offsetInCurrentLine += lengthToStyleRegionEnd;
			if (lineStartOffset + offsetInCurrentLine > currentTokenStyleRegion.endOffset) {
				styleRegionIndex++;
			}
			// The current code style region might be completely added now. In that case, the addCurrentStyleRegion has incremented to the next region.
			currentTokenStyleRegion = styleRegions[styleRegionIndex];
		}
	}
	return lineSegments;
}

/**
 * Returns the number of chars in the current line that come before the given target offset (the target offset is given
 * as chars from file begin). If the target offset is at or before the start of the current line, return 0. If the
 * target offset is after the end of the current line, returns the line length. Otherwise, returns the number of chars
 * in the current line before the target offset.
 */
function computeNumberOfLineCharsUntilOffset(
	targetOffset: number,
	localOffset: number,
	lineStartOffset: number,
	lineLength: number
): number {
	if (targetOffset <= lineStartOffset) {
		// The targetOffset is in a previous line or exactly the start of the current line.
		return 0;
	} else if (targetOffset > lineStartOffset + lineLength) {
		// The targetOffset is in some following line. Return the number of remaining chars in the current line (next line is handled separately).
		return lineLength - localOffset;
	}
	const currentOffsetFromFileStart = lineStartOffset + localOffset;
	// The targetOffset is in the current line.
	return targetOffset - currentOffsetFromFileStart;
}

function markBracesThatBelongTogether(linesWithTokens: Map<number, LineSegment[]>) {
	const openingBracesStack: CodeLineSegment[] = [];

	for (const segmentsInLine of linesWithTokens.values()) {
		segmentsInLine.forEach(segment => {
			if (!isCodeLineSegment(segment) || !segment.isDelimiter) {
				return;
			}
			if (StringUtils.equalsOneOf(segment.text.trim(), '(', '{', '[')) {
				openingBracesStack.push(segment);
			} else if (
				StringUtils.equalsOneOf(segment.text.trim(), ')', '}', ']') &&
				// This check is necessary because for code excerpts (e.g. in the search results, where we show
				// a limited number of lines), we can have 'irregular' brace constructs.
				openingBracesStack.length > 0
			) {
				const openingBrace = openingBracesStack.pop()!;
				segment.associatedBracketSegmentId = openingBrace.fileAndOffsetId;
				openingBrace.associatedBracketSegmentId = segment.fileAndOffsetId;
			}
		});
	}
}

/** Whether the given object is a {@link CodeLineSegment}. */
export function isCodeLineSegment(segment: LineSegment): segment is CodeLineSegment {
	return 'isIdentifier' in segment;
}

function createCodeLineSegment(
	segmentText: string,
	charOffsetInFile: number,
	project: string,
	cssStyleClass: string,
	isIdentifier: boolean,
	isDelimiter: boolean,
	preprocessorExpansions?: PreprocessorExpansionsTransport,
	fileIndex = 0
): CodeLineSegment {
	const linkTargetUniformPath = getIncludeDirectiveTargetFile(preprocessorExpansions, charOffsetInFile);
	let href;
	if (preprocessorExpansions && linkTargetUniformPath != null) {
		href = Links.code(project, linkTargetUniformPath, {
			commit: preprocessorExpansions.commit
		});
	}
	return {
		href,
		cssStyleClass,
		fileAndOffsetId: makeIdForFileAndOffset(fileIndex, charOffsetInFile),
		text: segmentText,
		offsetInFile: charOffsetInFile,
		isIdentifier,
		isDelimiter
	};
}

/**
 * Checks whether the token at the given offset is (1) an C/C++ include directive for which (2) the preprocessor found a
 * matching file and included it. If both conditions hold, this method returns the uniform path of the included file.
 * Otherwise it returns undefined.
 *
 * @param charOffsetInFile The (character-based) offset of the current token
 */
function getIncludeDirectiveTargetFile(
	preprocessorExpansions: PreprocessorExpansionsTransport | undefined,
	charOffsetInFile: number
): string | undefined {
	if (preprocessorExpansions?.offsetToExpansionGroup[charOffsetInFile] != null) {
		return preprocessorExpansions.expansionGroupToIncludedPath[
			preprocessorExpansions.offsetToExpansionGroup[charOffsetInFile]
		];
	}
	return undefined;
}

function createBasicLineSegment(
	lineContent: string,
	offsetInCurrentLine: number,
	lengthToNextToken: number,
	lineStartOffset: number,
	fileIndex = 0
): BasicLineSegment {
	const nonTokenContent = lineContent.substring(offsetInCurrentLine, offsetInCurrentLine + lengthToNextToken);
	const charOffsetInFile = lineStartOffset + offsetInCurrentLine;
	return {
		text: nonTokenContent,
		offsetInFile: lineStartOffset + offsetInCurrentLine,
		fileAndOffsetId: makeIdForFileAndOffset(fileIndex, charOffsetInFile)
	};
}

/**
 * Create a unique identifier for a file and offset.
 *
 * @returns A string id for the given file index and offset
 */
function makeIdForFileAndOffset(fileIndex: number, offsetInFile: number): string {
	return `file-${fileIndex}-token-${offsetInFile}`;
}
